import React, { useEffect, useState } from 'react';
import AdminModal from '../components/AdminModal';
import { deleteDiscount, fetchDiscounts, saveDiscount } from '../lib/adminApi';

function Discounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setDiscounts(await fetchDiscounts());
    } catch (err) {
      setError(err.message || 'Could not load discounts. Run supabase-admin.sql.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({
      code: '',
      description: '',
      discount_type: 'percent',
      value: '',
      active: true,
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!editing.code.trim()) {
      setError('Discount code is required.');
      return;
    }
    if (editing.value === '' || Number.isNaN(Number(editing.value))) {
      setError('Valid discount value is required.');
      return;
    }

    try {
      await saveDiscount({
        ...editing,
        code: editing.code.trim().toUpperCase(),
        value: Number(editing.value),
      });
      setEditing(null);
      setSuccess('Discount saved.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not save discount.');
    }
  };

  const handleDelete = async (discount) => {
    if (!window.confirm(`Delete discount "${discount.code}"?`)) return;
    try {
      await deleteDiscount(discount.id);
      setSuccess('Discount deleted.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete discount.');
    }
  };

  return (
    <div>
      <div className="admin-topbar" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="admin-page-title">Discounts</h1>
          <p className="admin-page-subtitle">Create and manage promotional codes</p>
        </div>
        <button type="button" className="admin-btn admin-btn-primary" onClick={openNew}>
          Add discount
        </button>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {success && <p className="admin-success-msg">{success}</p>}

      <div className="admin-card">
        {loading && <p className="admin-empty">Loading discounts...</p>}
        {!loading && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map(discount => (
                  <tr key={discount.id}>
                    <td style={{ fontWeight: 600 }}>{discount.code}</td>
                    <td>{discount.discount_type}</td>
                    <td>
                      {discount.discount_type === 'percent'
                        ? `${discount.value}%`
                        : `$${discount.value}`}
                    </td>
                    <td>
                      <span className={`admin-badge ${discount.active ? 'published' : 'hidden'}`}>
                        {discount.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                          onClick={() => setEditing(discount)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => handleDelete(discount)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!discounts.length && <p className="admin-empty">No discounts yet.</p>}
          </div>
        )}
      </div>

      {editing && (
        <AdminModal title={editing.id ? 'Edit discount' : 'Add discount'} onClose={() => setEditing(null)}>
          <form onSubmit={handleSave}>
            <div className="admin-field">
              <label className="admin-label">Code</label>
              <input
                className="admin-input"
                value={editing.code}
                onChange={e => setEditing(d => ({ ...d, code: e.target.value.toUpperCase() }))}
                required
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Description</label>
              <input
                className="admin-input"
                value={editing.description || ''}
                onChange={e => setEditing(d => ({ ...d, description: e.target.value }))}
              />
            </div>
            <div className="admin-grid-2">
              <div className="admin-field">
                <label className="admin-label">Type</label>
                <select
                  className="admin-input"
                  value={editing.discount_type}
                  onChange={e => setEditing(d => ({ ...d, discount_type: e.target.value }))}
                >
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </div>
              <div className="admin-field">
                <label className="admin-label">Value</label>
                <input
                  className="admin-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.value}
                  onChange={e => setEditing(d => ({ ...d, value: e.target.value }))}
                  required
                />
              </div>
            </div>
            <label className="admin-checkbox" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={editing.active !== false}
                onChange={e => setEditing(d => ({ ...d, active: e.target.checked }))}
              />
              Active
            </label>
            <button type="submit" className="admin-btn admin-btn-primary" style={{ width: '100%' }}>
              Save discount
            </button>
          </form>
        </AdminModal>
      )}
    </div>
  );
}

export default Discounts;
